import React, { useState, useEffect } from 'react';
import { getDoc } from 'firebase/firestore';
import { useAgency } from '../AgencyContext';
import { AutoAwesome } from '@mui/icons-material';

function GeneratePromptL1({ task, taskId }) {
  const [taskName, setTaskName] = useState('');
  const [prompts, setPrompts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const { getAgencyDoc } = useAgency();

  // Récupérer le nom de la tâche et les prompts depuis aitask
  useEffect(() => {
    const fetchTaskData = async () => {
      console.log('🔍 GeneratePromptL1 - Searching for task data:', {
        'task.taskId': task?.taskId,
        'task.taskName': task?.taskName,
        'task.name': task?.name,
        'fallback taskId': taskId
      });

      // Essayer d'abord avec task.taskId (référence vers aitask)
      if (task?.taskId) {
        try {
          // Récupérer les infos de base de la tâche via Agency Context
          console.log('🔍 Trying to access document via Agency Context: aitask/' + task.taskId);
          const taskDocRef = getAgencyDoc('aitask', task.taskId);
          if (!taskDocRef) {
            console.log('❌ Could not get agency document reference');
            return;
          }
          const taskDoc = await getDoc(taskDocRef);
          console.log('📄 Document exists:', taskDoc.exists());
          if (taskDoc.exists()) {
            const taskData = taskDoc.data();
            console.log('✅ Found task by taskId:', taskData.name);
            console.log('🔍 FULL TASK DOCUMENT DATA:');
            console.log(taskData);
            console.log('🔍 Checking if prompts field exists in document:', {
              hasPromptsField: !!taskData.prompts,
              promptsField: taskData.prompts,
              allFields: Object.keys(taskData),
              promptsType: typeof taskData.prompts,
              promptsIsObject: taskData.prompts && typeof taskData.prompts === 'object'
            });
            setTaskName(taskData.name);
            
            // Les prompts sont dans le champ 'prompts' du document
            if (taskData.prompts) {
              console.log('✅ Found prompts in document field:', {
                promptsKeys: Object.keys(taskData.prompts),
                promptsData: taskData.prompts
              });
              setPrompts(taskData.prompts);
            } else {
              console.log('❌ No prompts field found in document');
              setPrompts({});
            }
          }
        } catch (error) {
          console.error('Error fetching by taskId:', error);
        }
      }

      // Fallback: utiliser task.taskName si disponible
      if (task?.taskName) {
        console.log('📝 Using taskName from task object:', task.taskName);
        setTaskName(task.taskName);
      }
    };

    if (task) {
      fetchTaskData();
    }
  }, [task, taskId, getAgencyDoc]);

  // Fonction pour sélectionner un élément aléatoire d'un array avec pondération
  const getRandomFromArray = (arr) => {
    if (!arr || arr.length === 0) return '';
    
    // Créer un array pondéré basé sur les poids
    const weightedArray = [];
    
    arr.forEach((item) => {
      const value = item?.value || item;
      const weight = item?.weight || 1;
      
      // Ajouter l'élément 'weight' fois dans le tableau pondéré
      for (let i = 0; i < weight; i++) {
        weightedArray.push(value);
      }
    });
    
    console.log('🎲 Weighted array for selection:', {
      originalLength: arr.length,
      weightedLength: weightedArray.length,
      weights: arr.map(item => ({ value: item?.value || item, weight: item?.weight || 1 }))
    });
    
    // Sélectionner aléatoirement dans le tableau pondéré
    const randomIndex = Math.floor(Math.random() * weightedArray.length);
    return weightedArray[randomIndex];
  };

  // Générer une prompt complète
  const generatePrompt = () => {
    if (!prompts) return '';

    console.log('🎯 Generating prompt with data:', prompts);

    // Check if we have a custom template
    if (prompts.promptTemplate && typeof prompts.promptTemplate === 'string') {
      console.log('🎯 Using custom template:', prompts.promptTemplate);
      
      // Get random values for replacement
      const randomValues = {
        outfit: getRandomFromArray(prompts.outfit),
        shoes: getRandomFromArray(prompts.shoes),
        location: getRandomFromArray(prompts.location),
        pose: getRandomFromArray(prompts.pose),
        photography: getRandomFromArray(prompts.photography),
        quantity: getRandomFromArray(prompts.quantity),
        light: getRandomFromArray(prompts.light),
        eyes: getRandomFromArray(prompts.eyes),
        age: getRandomFromArray(prompts.age),
        skin: getRandomFromArray(prompts.skin),
        hairs: getRandomFromArray(prompts.hairs),
        mood: getRandomFromArray(prompts.mood)
      };

      // Generate personality sub-template if it exists
      let personalityValue = '';
      if (prompts.personalityTemplate && typeof prompts.personalityTemplate === 'string') {
        personalityValue = prompts.personalityTemplate;
        // Replace personality sub-placeholders
        Object.entries(randomValues).forEach(([key, value]) => {
          if (['eyes', 'age', 'skin', 'hairs', 'mood'].includes(key) && value) {
            const placeholder = `{${key}}`;
            personalityValue = personalityValue.replace(new RegExp(placeholder, 'g'), value);
          }
        });
      }
      
      // Add processed personality to randomValues
      randomValues.personality = personalityValue;

      console.log('🎯 Random values for template:', randomValues);

      // Replace placeholders in the template
      let templatePrompt = prompts.promptTemplate;
      
      // Replace all placeholders
      Object.entries(randomValues).forEach(([key, value]) => {
        if (value) {
          const placeholder = `{${key}}`;
          templatePrompt = templatePrompt.replace(new RegExp(placeholder, 'g'), value);
        }
      });

      console.log('🎯 Generated from template:', templatePrompt);
      return templatePrompt.trim();
    }

    // No template found, return empty
    console.log('❌ No template found in prompts');
    return '';
  };

  const handleGenerateClick = async () => {
    setLoading(true);
    
    if (!prompts) {
      setLoading(false);
      return;
    }

    const generatedPrompt = generatePrompt();
    
    if (generatedPrompt) {
      try {
        await navigator.clipboard.writeText(generatedPrompt);
        setGeneratedPrompt(generatedPrompt);
        console.log('🎯 Generated prompt:', generatedPrompt);
        
        
        setTimeout(() => {
          setGeneratedPrompt('');
        }, 4000);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
    
    setLoading(false);
  };

  const hasPrompts = prompts && Object.keys(prompts).length > 0;
  
  // Debug des prompts dans le render
  console.log('🎯 RENDER DEBUG:', {
    prompts: prompts,
    promptsType: typeof prompts,
    promptsIsNull: prompts === null,
    promptsKeys: prompts ? Object.keys(prompts) : 'NO PROMPTS',
    hasPrompts: hasPrompts,
    buttonState: hasPrompts ? 'ENABLED' : 'DISABLED'
  });

  return (
    <div style={{ 
      position: 'absolute', 
      top: '24px', 
      right: '24px', 
      zIndex: 10 
    }}>
      <button 
        onClick={handleGenerateClick}
        disabled={loading || !hasPrompts}
        style={{
          padding: '12px 16px',
          background: hasPrompts ? '#555555' : '#404040',
          color: hasPrompts ? 'white' : '#8e8e93',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: hasPrompts ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: '120px',
          justifyContent: 'center',
          transition: 'all 0.2s ease'
        }}
        title={hasPrompts ? 'Generate random prompt' : 'No prompts configured for this task'}
      >
        <AutoAwesome style={{ fontSize: 16 }} />
        Generate
      </button>
      
      
      {/* Generated Prompt Display */}
      {generatedPrompt && (
        <div className="l1__generated-prompt-display">
          {generatedPrompt}
        </div>
      )}
      
    </div>
  );
}

export default GeneratePromptL1;